import React, { ReactNode } from 'react';
import {
  AccountingIcon,
  FireIcon,
  ForumIcon,
  HospitalParametersIcon,
  ImportPatientsIcon,
  InfoIcon,
  MessagesIcon,
  PendingUserIcon,
  PeropIcon,
  PostopIcon,
  PreopIcon,
  ProtocolsIcon,
  SubscribeIcon
} from './routeIcons';
import { IsEnabledPredicate, pagePermissions } from './permissions';

interface BaseNav {
  text: string;
  icon?: ReactNode;
  isEnabled?: IsEnabledPredicate;
  isNew?: boolean; // Not implemented for ParentNavs. TODO if needed
}

export interface LeafNav extends BaseNav {
  path: string;
}

export interface ParentNav extends BaseNav {
  parentPath: string;
  childNavs: NavItemData[];
}

export type DividerSignal = 'divider';

export type NavItemData = LeafNav | ParentNav;

export const navItems: (NavItemData | DividerSignal)[] = [
  {
    text: 'doctor-dashboard.nav.preop',
    icon: <PreopIcon />,
    parentPath: '/patients/preop',
    childNavs: [
      {
        text: 'doctor-dashboard.nav.preop.all',
        path: '',
        isEnabled: pagePermissions.isNotUsap
      },
      {
        text: 'doctor-dashboard.nav.preop.surgeryPlanning',
        path: '/surgery-planning'
      },
      {
        text: 'doctor-dashboard.nav.preop.telehealth',
        path: '/telehealth',
        isEnabled: pagePermissions.isNotUsap
      },
      {
        text: 'doctor-dashboard.nav.preop.cardiac',
        path: '/stress-test',
        isEnabled: pagePermissions.isNotUsap
      },
      {
        text: 'doctor-dashboard.nav.preop.testing',
        path: '/lab-testing',
        isEnabled: pagePermissions.isNotUsap
      },
      {
        text: 'doctor-dashboard.nav.preop.anemic',
        path: '/anemic',
        isEnabled: pagePermissions.isNotUsap
      },
      {
        text: 'doctor-dashboard.nav.preop.smokers',
        path: '/smokers',
        isEnabled: pagePermissions.isNotUsap
      },
      {
        text: 'doctor-dashboard.nav.preop.malnutrition',
        path: '/malnutrition',
        isEnabled: pagePermissions.isNotUsap
      },
      {
        text: 'doctor-dashboard.nav.preop.highOsa',
        path: '/high-osa',
        isEnabled: pagePermissions.isNotUsap
      },
      {
        text: 'doctor-dashboard.nav.preop.chronicPain',
        path: '/chronic-pain',
        isEnabled: [
          'and',
          pagePermissions.chronicPain,
          pagePermissions.isNotUsap
        ]
      }
    ]
  },
  {
    text: 'doctor-dashboard.nav.perop',
    icon: <PeropIcon />,
    parentPath: '/patients/perop',
    childNavs: [
      {
        text: 'doctor-dashboard.nav.preop.all',
        path: ''
      }
    ]
  },
  {
    text: 'doctor-dashboard.nav.postop',
    icon: <PostopIcon />,
    parentPath: '/patients/postop',
    childNavs: [
      {
        text: 'doctor-dashboard.nav.preop.all',
        path: ''
      },
      {
        text: 'doctor-dashboard.nav.postop.early',
        path: '/early',
        isEnabled: pagePermissions.earlyPostop
      },
      {
        text: 'doctor-dashboard.nav.postop.chronicPain',
        path: '/chronic-pain',
        isEnabled: pagePermissions.chronicPain
      }
    ]
  },
  {
    text: 'doctor-dashboard.nav.messaging',
    icon: <MessagesIcon />,
    path: '/messaging',
    isEnabled: pagePermissions.isNotUsap
  },
  {
    text: 'doctor-dashboard.nav.accounting',
    icon: <AccountingIcon />,
    parentPath: '/accounting',
    isEnabled: ['and', pagePermissions.billing, pagePermissions.isNotUsap],
    childNavs: [
      {
        text: 'doctor-dashboard.nav.accounting.billing',
        path: '/billing'
      }
    ]
  },
  {
    text: 'doctor-dashboard.nav.protocols',
    icon: <ProtocolsIcon />,
    path: 'https://doc.clickup.com/20523930/d/h/kjawu-42855/da23da78a53b99d'
  },
  'divider',
  {
    text: 'doctor-dashboard.nav.hospitalParameters',
    icon: <HospitalParametersIcon />,
    parentPath: '/hospital-params',
    isEnabled: pagePermissions.isNotUsap,
    childNavs: [
      {
        text: 'doctor-dashboard.nav.hospitalParameters.surgeries',
        path: '/surgeries'
      },
      {
        text: 'doctor-dashboard.nav.hospitalParameters.documentTemplates',
        path: '/document-templates',
        isEnabled: pagePermissions.customDocument
      },
      {
        text: 'doctor-dashboard.hospitalParams.magicKeyboard',
        path: '/magic-keyboard'
      },
      {
        text: 'doctor-dashboard.nav.hospitalParameters.mhoSuggestions',
        path: '/mho-suggestions'
      },
      {
        text: 'doctor-dashboard.nav.hospitalParameters.premedicationProtocols',
        path: '/premedication-protocols',
        isEnabled: pagePermissions.premedication
      },
      {
        text: 'doctor-dashboard.nav.hospitalParameters.billing',
        path: '/billing',
        isEnabled: pagePermissions.billing
      }
    ]
  },
  {
    text: 'doctor-dashboard.nav.settings',
    icon: <ImportPatientsIcon />,
    path: 'https://hoopcare.typeform.com/importer',
    isEnabled: pagePermissions.isNotUsap
  },
  {
    text: 'doctor-dashboard.nav.pendingPatients',
    icon: <PendingUserIcon />,
    path: '/pending-patients',
    isEnabled: pagePermissions.isNotUsap
  },
  {
    text: 'doctor-dashboard.nav.roadmap',
    icon: <FireIcon />,
    path: 'https://hoopcare.canny.io/feature-requests',
    isEnabled: pagePermissions.isNotUsap
  },
  {
    text: 'doctor-dashboard.nav.subscribe',
    icon: <SubscribeIcon />,
    path: 'https://hoopcare.typeform.com/subscribe',
    isEnabled: pagePermissions.isNotUsap
  },
  {
    text: 'doctor-dashboard.nav.forum',
    icon: <ForumIcon />,
    path: '',
    isEnabled: pagePermissions.isNotUsap
  },
  {
    text: 'doctor-dashboard.nav.tutorials',
    icon: <InfoIcon />,
    path: '/tutorial',
    isNew: false,
    isEnabled: pagePermissions.isNotUsap
  }
];

export const isParentNav = (nav: NavItemData): nav is ParentNav =>
  !!nav && typeof nav === 'object' && 'childNavs' in nav;

export const isLeafNav = (nav: NavItemData): nav is LeafNav =>
  !!nav && typeof nav === 'object' && !('childNavs' in nav);

export const isDividerSignal = (nav: any): nav is DividerSignal =>
  nav === 'divider';
